import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TrustIndicators from "@/components/TrustIndicators";
import FeaturedProjects from "@/components/FeaturedProjects";
import QualityMaterials from "@/components/QualityMaterials";
import Footer from "@/components/Footer";
import SeoMeta from "@/components/SeoMeta";
import OptimizedImage from "@/components/OptimizedImage";
import HeroBackground from "@/components/HeroBackground";

const KonyaMap = lazy(() => import("@/components/KonyaMap"));

const MapFallback = () => (
  <section className="py-32 bg-[#000000]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="h-[280px] md:h-[380px] border border-[#C5A059]/15 bg-gradient-to-br from-[#0A0A0A] via-[#141414] to-[#0A0A0A] flex items-center justify-center text-[#86868B] text-xs md:text-sm tracking-[0.18em] uppercase">
        Harita Yukleniyor
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      <SeoMeta
        title="Konyap Mühendislik | Konya İnşaat ve Villa Projeleri"
        description="Konya'da modern konut ve villa projeleri üreten Konyap Mühendislik. Saklı Bahçe, Villa Selver, Konyap Apartmanı ve diğer prestijli projeler. Meram, Selçuklu, Karatay'da kaliteli inşaat."
        path="/"
        image="/images/2-anasayfa/anasayfa.jpg"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Konyap Mühendislik | Konya İnşaat ve Villa Projeleri",
          "description": "Konya'da modern konut ve villa projeleri üreten Konyap Mühendislik. Saklı Bahçe, Villa Selver, Konyap Apartmanı ve diğer prestijli projeler.",
          "url": "https://konyapmuhendislik.com/",
          "inLanguage": "tr-TR",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://konyapmuhendislik.com/" }
            ]
          }
        }}
      />
      {/* Hero Section */}
      <section
        className="relative h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        <HeroBackground src="/images/2-anasayfa/anasayfa_opt.webp" position="center center" />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ willChange: "transform, opacity" }}
          >
            <h1 className="font-light text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-[#F5F5F7] mb-6">
              Geleceği İnşa Ediyoruz
            </h1>
            <p className="text-[#86868B] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Konya'nın en prestijli lokasyonlarında, mimari mükemmellik ve
              mühendislik hassasiyetiyle yaşam alanları yaratıyoruz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/projeler")}
                className="bg-[#C5A059] hover:bg-[#D4AF37] text-black px-8 py-4 text-sm font-medium transition-all duration-200 hover:scale-105"
                data-testid="hero-cta-button"
              >
                Projelerimizi Keşfedin
              </button>
              <button
                onClick={() => navigate("/iletisim")}
                className="bg-transparent border border-white/30 hover:border-white text-[#F5F5F7] px-8 py-4 text-sm font-medium transition-all duration-200"
                data-testid="hero-secondary-button"
              >
                İletişime Geçin
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand Positioning */}
      <section
        className="py-32 bg-[#050505]"
        data-testid="brand-positioning-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mb-6">
              KİMLİK
            </p>
            <h2 className="font-light text-4xl md:text-5xl tracking-tight text-[#F5F5F7] mb-8">
              Güçlü Temeller,
              <br />
              Güçlü Yarınlar
            </h2>
            <p className="text-[#86868B] text-base md:text-lg leading-relaxed">
              5 yılı aşkın sektör deneyimimizle, Konya'nın en saygın inşaat
              firmalarından biri olarak konumlandık. Her projede kalite,
              güvenilirlik ve mimari estetik anlayışını bir araya getiriyoruz.
              Müşterilerimize sadece ev değil, güvenli ve konforlu yaşam
              alanları sunuyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <TrustIndicators />

      {/* Featured Projects */}
      <FeaturedProjects />

      {/* Konya Map */}
      <Suspense fallback={<MapFallback />}>
        <KonyaMap />
      </Suspense>

      {/* Material Quality & Construction Standards Section */}
      <section
        className="py-32 bg-[#000000] relative overflow-hidden"
        data-testid="material-standards-section"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#1A1410] to-[#000000]" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-6 font-light">
              MALZEME KALİTESİ
            </p>
            <h2 className="font-light text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#F5F5F7] mb-8 leading-tight">
              Yapının Gücü,
              <br />
              Yaşamın Kalitesi
            </h2>
            <p className="text-[#86868B] text-base md:text-lg font-light leading-relaxed max-w-3xl mx-auto mb-8">
              Güçlü yapı sistemlerinden zarif iç mekan detaylarına kadar her
              malzemeyi özenle seçiyoruz.
            </p>
            <div className="inline-block bg-gradient-to-r from-[#C5A059]/10 via-[#C5A059]/5 to-transparent backdrop-blur-sm border-l-2 border-[#C5A059] px-4 md:px-8 py-4 max-w-full">
              <p className="text-[#F5F5F7] text-lg md:text-2xl font-light italic">
                "Yapının gücü temelden, yaşam kalitesi detaylardan gelir."
              </p>
            </div>
          </motion.div>

          {/* Yapısal Kalite */}
          <div className="mb-32">
            <div className="home-material-top-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  img: "/images/2-anasayfa/malzeme_opt.webp",
                  title: "Malzeme Kalitesi",
                  items: [
                    "Seçkin ve sertifikalı yapı malzemeleri",
                    "Uzun ömürlü ve dayanıklı yüzeyler",
                    "Kalite standartlarına uygun üretim",
                  ],
                },
                {
                  img: "/images/2-anasayfa/mimari_kalite_opt.webp",
                  title: "Mimari Kalite",
                  items: [
                    "Dengeli ve işlevsel planlama",
                    "Işık, hacim ve oran uyumu",
                    "Modern ve zamansız tasarım anlayışı",
                  ],
                },
                {
                  img: "/images/2-anasayfa/uygulama_kalitesi_opt.webp",
                  title: "Uygulama Kalitesi",
                  items: [
                    "Titiz ve kontrollü uygulama süreci",
                    "Tüm detaylarda hassas işçilik",
                    "Başlangıçtan teslimata aynı kalite standardı",
                  ],
                },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <div className="relative h-80 overflow-hidden mb-6">
                    <OptimizedImage
                      src={card.img}
                      alt={card.title}
                      className="w-full h-full object-cover image-hover-zoom"
                      rootMargin="1000px"
                      loading={i === 0 ? "eager" : "lazy"}
                      priority={i === 0}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <h4 className="text-[#F5F5F7] text-2xl font-light tracking-tight mb-2">
                        {card.title}
                      </h4>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {card.items.map((item, j) => (
                      <li
                        key={j}
                        className="text-[#F5F5F7] text-sm font-light flex items-center gap-2"
                      >
                        <div className="w-1 h-1 rounded-full bg-[#C5A059]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-[#C5A059]/30 to-transparent mb-32" />

          {/* İç Mekan Kalitesi */}
          <div className="mb-32">
            <div className="home-material-bottom-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  img: "/images/2-anasayfa/mermer2_opt.webp",
                  title: "Mermer & Doğal Taş",
                  items: [
                    "Seçkin doğal taş ve mermer yüzeyler",
                    "Dayanıklı ve uzun ömürlü kullanım",
                    "Estetik ve kaliteyi birleştiren dokular",
                  ],
                },
                {
                  img: "/images/2-anasayfa/zemin_sistemleri_opt.webp",
                  title: "Zemin Sistemleri",
                  items: [
                    "Isı ve ses konforu sağlayan altyapı",
                    "Sessiz ve dengeli kullanım deneyimi",
                    "Yüksek dayanımlı parke ve kaplamalar",
                  ],
                },
                {
                  img: "/images/2-anasayfa/mutfak-banyo_opt.webp",
                  title: "Mutfak & Banyo",
                  items: [
                    "Modern ve işlevsel tasarım çözümleri",
                    "Kaliteli seramik ve yüzey malzemeleri",
                    "Günlük kullanıma uygun dayanıklı sistemler",
                  ],
                },
                {
                  img: "/images/2-anasayfa/icmekan_opt.webp",
                  title: "İç Mekan Detayları",
                  items: [
                    "Her noktada hissedilen malzeme kalitesi",
                    "Işık, yüzey ve form uyumu",
                    "Mekanı tamamlayan ince işçilik detayları",
                  ],
                },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <div className="relative h-72 overflow-hidden mb-4">
                    <OptimizedImage
                      src={card.img}
                      alt={card.title}
                      className="w-full h-full object-cover image-hover-zoom"
                      rootMargin="1000px"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h4 className="text-[#F5F5F7] text-lg font-light tracking-tight">
                        {card.title}
                      </h4>
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {card.items.map((item, j) => (
                      <li
                        key={j}
                        className="text-[#D0D0D0] text-xs font-light flex items-center gap-2"
                      >
                        <div className="w-0.5 h-0.5 rounded-full bg-[#C5A059]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-[#C5A059]/5 to-transparent backdrop-blur-xl border border-[#C5A059]/20 p-6 md:p-12">
              <p className="text-[#F5F5F7] text-base md:text-2xl font-light leading-relaxed mb-8 text-center">
                Yapılarımızda kullandığımız her malzeme, hem yapısal güvenlik
                hem de günlük yaşam konforu için özenle seçilir.
              </p>
              <p className="text-[#C5A059] text-sm tracking-wider uppercase font-light text-center">
                — Konyap Mühendislik Malzeme Kalite Standardı
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-32 bg-[#050505]" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glassmorphic p-8 md:p-16 text-center"
          >
            <h2 className="font-light text-4xl md:text-5xl tracking-tight text-[#F5F5F7] mb-6">
              Hayalinizdeki Evi
              <br />
              Birlikte İnşa Edelim
            </h2>
            <p className="text-[#86868B] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Uzman ekibimiz sizinle iletişime geçmek için hazır. Projelerimiz
              hakkında detaylı bilgi alın.
            </p>
            <button
              onClick={() => navigate("/iletisim")}
              className="bg-[#C5A059] hover:bg-[#D4AF37] text-black px-10 py-4 text-sm font-medium transition-all duration-200 hover:scale-105"
              data-testid="final-cta-button"
            >
              Hemen İletişime Geçin
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
